import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Space,
  Typography,
  Button,
  Tag,
  Table,
  Input,
  Select,
  DatePicker,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  listCampaigns,
  updateCampaign,
  downloadConsolidatedCampaignReport,
} from "../../../redux/actions/campaignsAction";
import { listTags } from "../../../redux/actions/contactsAction";

import FormCampaign from "../../../components/forms/formCampaign";
import ViewCampaign from "../../../components/viewer/viewCampaign";
import DeleteRecord from "../../../components/modals/deleteRecord";
import SendEmail from "../../../components/modals/sendEmail";
import CopyCampaign from "../../../components/modals/copyCampaign";
import { canWriteResources } from "../../../utils/rbac";
import useTableScrollY from "../../../hooks/useTableScrollY";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_PAGINATION = { current: 1, pageSize: 10 };

const STATUS_OPTIONS = [
  { value: "Draft", label: "Draft" },
  { value: "Scheduled", label: "Scheduled" },
  { value: "Pending", label: "Pending" },
  { value: "Sent", label: "Sent" },
  { value: "Failed", label: "Failed" },
];

const DATE_RANGE_OPTIONS = [
  { value: "", label: "All time" },
  { value: "7", label: "Last 7 days" },
  { value: "28", label: "Last 28 days" },
  { value: "90", label: "Last 90 days" },
  { value: "custom", label: "Custom range" },
];

// Resolves a preset ("7"/"28"/"90") or a custom [start, end] dayjs pair into
// ISO createdFrom/createdTo bounds for the API. Returns {} for "all time".
function resolveDateBounds(preset, customRange) {
  if (preset === "custom") {
    const [start, end] = customRange || [];
    return {
      createdFrom: start ? start.startOf("day").toISOString() : undefined,
      createdTo: end ? end.endOf("day").toISOString() : undefined,
    };
  }
  const days = parseInt(preset, 10);
  if (!days) return {};
  return {
    createdFrom: dayjs().subtract(days, "day").startOf("day").toISOString(),
    createdTo: dayjs().endOf("day").toISOString(),
  };
}

export default function Campaigns() {
  const canWrite = canWriteResources();
  const dispatch = useDispatch();
  const campaigns = useSelector((state) => state.campaigns.campaigns);
  const campaignCount = useSelector((state) => state.campaigns.campaignCount);
  const availableTags = useSelector((state) => state.contacts.tags);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [dateRangePreset, setDateRangePreset] = useState("");
  const [customRange, setCustomRange] = useState(null);
  const [tableParams, setTableParams] = useState({
    pagination: {
      ...DEFAULT_PAGINATION,
      total: 0,
    },
  });
  const paginationRef = useRef(tableParams.pagination);
  const [tableAreaRef, tableScrollY] = useTableScrollY([
    loading,
    campaigns.length,
  ]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [downloadingConsolidatedReport, setDownloadingConsolidatedReport] =
    useState(false);

  // Snapshot of the current filter state, used as the default `filters`
  // arg to fetchCampaigns so callers don't need to repeat every field.
  const currentFilters = useCallback(
    () => ({
      search: searchInput,
      tags: selectedTags,
      status: selectedStatus,
      dateRangePreset,
      customRange,
    }),
    [searchInput, selectedTags, selectedStatus, dateRangePreset, customRange],
  );

  const fetchCampaigns = useCallback(
    (pagination, filters = {}) => {
      const nextPagination = pagination || paginationRef.current;
      paginationRef.current = nextPagination;
      const search = filters.search ?? searchInput;
      const tags = filters.tags ?? selectedTags;
      const status = filters.status ?? selectedStatus;
      const preset = filters.dateRangePreset ?? dateRangePreset;
      const range = filters.customRange ?? customRange;

      setLoading(true);
      const payload = {
        pageSize: nextPagination.pageSize,
        pageNum: nextPagination.current,
      };
      if (search?.trim()) {
        payload.search = search.trim();
      }
      if (tags?.length) {
        payload.tags = tags.join(",");
      }
      if (status) {
        payload.status = status;
      }
      const { createdFrom, createdTo } = resolveDateBounds(preset, range);
      if (createdFrom) payload.createdFrom = createdFrom;
      if (createdTo) payload.createdTo = createdTo;

      return dispatch(listCampaigns(payload))
        .then(() => {
          setTableParams({
            pagination: { ...nextPagination },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [dispatch, searchInput, selectedTags, selectedStatus, dateRangePreset, customRange],
  );

  const handleListChange = useCallback(
    (pagination) => {
      fetchCampaigns(pagination, currentFilters());
    },
    [fetchCampaigns, currentFilters],
  );

  const applyFilters = () => {
    fetchCampaigns({ ...tableParams.pagination, current: 1 }, currentFilters());
  };

  const clearFilters = () => {
    setSearchInput("");
    setSelectedTags([]);
    setSelectedStatus(null);
    setDateRangePreset("");
    setCustomRange(null);
    fetchCampaigns(
      { ...tableParams.pagination, current: 1 },
      { search: "", tags: [], status: null, dateRangePreset: "", customRange: null },
    );
  };

  const cancelSchedule = (record) => {
    dispatch(
      updateCampaign(
        {
          status: { type: "warning", description: "Draft" },
          subject: record.subject,
          message: record.message,
          tags: record.tags,
        },
        record._id,
      ),
    ).then(() => fetchCampaigns(tableParams.pagination, currentFilters()));
  };

  const downloadConsolidatedReport = async () => {
    setDownloadingConsolidatedReport(true);
    try {
      const res = await dispatch(
        downloadConsolidatedCampaignReport(selectedRowKeys),
      );
      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "campaigns-consolidated-report.xlsx";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Failed to generate consolidated report");
    } finally {
      setDownloadingConsolidatedReport(false);
    }
  };

  const hasActiveFilters =
    Boolean(searchInput.trim()) ||
    selectedTags.length > 0 ||
    Boolean(selectedStatus) ||
    Boolean(dateRangePreset);

  const columns = [
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <>
          {record.status && (
            <Tag color={record.status.type}>{record.status.description}</Tag>
          )}
          {record.status?.description === "Sent" && (
            <>
              <br />
              <Text>
                <small>{new Date(record.updatedAt).toLocaleString()}</small>
              </Text>
            </>
          )}
          {record.status?.description === "Scheduled" &&
            record.scheduledSendAt && (
              <>
                <br />
                <Text>
                  <small>
                    {new Date(record.scheduledSendAt).toLocaleString()}
                  </small>
                </Text>
              </>
            )}
        </>
      ),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (_, record) => (
        <>
          {record.tags?.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </>
      ),
    },
    {
      title: "Creation",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (_, record) => (
        <Text>
          {new Date(record.createdAt).toLocaleString()}
          <br />
          <small>{record.createdBy}</small>
        </Text>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "left",
      width: 280,
      render: (_, record) => (
        <Space size="small" wrap>
          <ViewCampaign
            campaignId={record._id}
            fetchRecords={() =>
              fetchCampaigns(tableParams.pagination, currentFilters())
            }
          />
          {canWrite ? (
            <>
              <CopyCampaign
                campaignId={record._id}
                fetchRecords={() =>
                  fetchCampaigns(tableParams.pagination, currentFilters())
                }
              />
              {record.status?.description === "Draft" && (
                <FormCampaign
                  type="edit"
                  formData={record}
                  fetchRecords={() =>
                    fetchCampaigns(tableParams.pagination, currentFilters())
                  }
                />
              )}
              {record.status?.description === "Draft" && (
                <SendEmail
                  id={record._id}
                  subject={record.subject}
                  fetchRecords={() =>
                    fetchCampaigns(tableParams.pagination, currentFilters())
                  }
                />
              )}
              {record.status?.description === "Scheduled" && (
                <Button
                  danger
                  title="Cancel scheduled send"
                  onClick={() => cancelSchedule(record)}
                >
                  Cancel
                </Button>
              )}
              <DeleteRecord
                id={record._id}
                collection="campaigns"
                fetchRecords={() =>
                  fetchCampaigns(tableParams.pagination, currentFilters())
                }
              />
            </>
          ) : null}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    dispatch(listTags());
    fetchCampaigns(DEFAULT_PAGINATION, {
      search: "",
      tags: [],
      status: null,
      dateRangePreset: "",
      customRange: null,
    });
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const tagOptions = (availableTags || [])
    .filter(Boolean)
    .map((tag) => ({ value: tag, label: tag }));

  const hasPending = campaigns.some((c) =>
    ["Pending", "Scheduled"].includes(c.status?.description),
  );

  return (
    <div className="Campaigns">
      <Space
        wrap
        style={{
          display: "flex",
          margin: "0 0.5rem 0.25rem 0.5rem",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Title level={5} style={{ marginBottom: 0 }}>
            Campaigns
          </Title>
          <Text type="secondary">Manage your email campaigns</Text>
        </div>
        <Space size="small">
          {selectedRowKeys.length >= 2 ? (
            <Button
              type="primary"
              loading={downloadingConsolidatedReport}
              onClick={downloadConsolidatedReport}
            >
              Generate Report ({selectedRowKeys.length} selected)
            </Button>
          ) : null}
          {hasPending ? (
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                fetchCampaigns(tableParams.pagination, currentFilters())
              }
            >
              Refresh
            </Button>
          ) : null}
          {/* GrapesJS "Editor" entry point is hidden for now — it doesn't
              handle TinyMCE-authored content well and needs a design
              rework before it's ready to surface again. Route still
              exists at /app/editor. */}
          {canWrite ? (
            <FormCampaign
              type="create"
              fetchRecords={() =>
                fetchCampaigns(tableParams.pagination, currentFilters())
              }
            />
          ) : null}
        </Space>
      </Space>

      <Space wrap style={{ margin: "0 0.5rem 1rem 0.5rem", width: "100%" }}>
        <Input.Search
          placeholder="Search subject or creator"
          allowClear
          className="app-filter-control"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={applyFilters}
        />
        <Select
          mode="multiple"
          placeholder="Filter by tags"
          allowClear
          style={{ minWidth: 220 }}
          value={selectedTags}
          options={tagOptions}
          onChange={setSelectedTags}
          maxTagCount="responsive"
        />
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ minWidth: 160 }}
          value={selectedStatus}
          options={STATUS_OPTIONS}
          onChange={setSelectedStatus}
        />
        <Select
          placeholder="Date range"
          style={{ minWidth: 160 }}
          value={dateRangePreset}
          options={DATE_RANGE_OPTIONS}
          onChange={(value) => {
            setDateRangePreset(value);
            if (value !== "custom") setCustomRange(null);
          }}
        />
        {dateRangePreset === "custom" ? (
          <RangePicker
            value={customRange}
            onChange={setCustomRange}
            disabledDate={(current) => current && current > dayjs().endOf("day")}
          />
        ) : null}
        <Button type="primary" onClick={applyFilters}>
          Apply
        </Button>
        {hasActiveFilters ? (
          <Button onClick={clearFilters}>Clear</Button>
        ) : null}
      </Space>

      <div ref={tableAreaRef} style={{ width: "100%" }}>
        <Table
          loading={loading}
          dataSource={campaigns}
          scroll={{
            x: 1000,
            y: tableScrollY,
          }}
          columns={columns}
          rowKey="_id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (record) => ({
              disabled: record.status?.description !== "Sent",
            }),
          }}
          pagination={{
            ...tableParams.pagination,
            total: campaignCount,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (totalCount) => `Total  ${totalCount}  items`,
          }}
          onChange={handleListChange}
        />
      </div>
    </div>
  );
}
